import { Link } from "react-router-dom";
import LegalDocument from "../components/LegalDocument";

export default function Privacy() {
  return (
    <LegalDocument title="Privacy policy">
      <p className="legal-body__meta">
        Last updated: April 4, 2026. This policy is generic boilerplate—replace with counsel-reviewed
        text and accurate data practices before production use.
      </p>

      <h2>Who we are</h2>
      <p>
        HAB LOG (“we,” “us”) operates the Service. The data controller is the entity operating the
        deployment you use; contact details should be published by that operator.
      </p>

      <h2>Information we collect</h2>
      <p>
        <strong>Account data:</strong> such as email address, authentication identifiers, and
        preferences you provide.
      </p>
      <p>
        <strong>Content:</strong> recordings, transcripts, search queries, and related metadata you
        create or upload.
      </p>
      <p>
        <strong>Technical data:</strong> such as IP address, device/browser type, approximate location
        derived from network data, timestamps, and diagnostic logs needed to secure and operate the
        Service.
      </p>

      <h2>How we use information</h2>
      <p>
        We use data to provide and improve the Service, authenticate users, process and index content
        (including machine-learning or embedding features described in the product), prevent abuse,
        comply with law, and communicate with you about the Service.
      </p>

      <h2>AI models and inference</h2>
      <p>
        Transcription, text embeddings, and optional natural-language answers are produced using{" "}
        <strong>Cloudflare Workers AI</strong> on infrastructure operated by Cloudflare. This Service
        does not call OpenAI, Meta, or BAAI APIs directly for those features; inference runs through
        Workers AI as configured for the deployment.
      </p>
      <p>The following Workers AI model identifiers are in use:</p>
      <ul>
        <li>
          <code>@cf/openai/whisper-large-v3-turbo</code> — speech-to-text from audio associated with
          your content.
        </li>
        <li>
          <code>@cf/baai/bge-small-en-v1.5</code> — embeddings for transcript chunks used for search
          and indexing.
        </li>
        <li>
          <code>@cf/meta/llama-3.2-1b-instruct</code> — optional short answers when you use search;
          retrieved matches are still returned if generation is unavailable.
        </li>
      </ul>
      <p>
        <strong>Cloudflare (inference operator):</strong>{" "}
        <a
          href="https://developers.cloudflare.com/workers-ai/platform/privacy/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Your Data and Workers AI
        </a>
        ;{" "}
        <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">
          Cloudflare Privacy Policy
        </a>
        ;{" "}
        <a href="https://www.cloudflare.com/website-terms/" target="_blank" rel="noopener noreferrer">
          Cloudflare Website Terms of Use
        </a>
        .
      </p>
      <p>
        <strong>OpenAI (Whisper model lineage):</strong> The speech model is based on OpenAI’s
        Whisper family. OpenAI’s policies describe their practices as a model originator; your
        content is not sent by us to OpenAI’s consumer or API products for this feature. See{" "}
        <a href="https://openai.com/policies/privacy-policy/" target="_blank" rel="noopener noreferrer">
          OpenAI Privacy Policy
        </a>{" "}
        and{" "}
        <a href="https://openai.com/policies/terms-of-use/" target="_blank" rel="noopener noreferrer">
          OpenAI Terms of Use
        </a>
        .
      </p>
      <p>
        <strong>Meta (Llama):</strong> The optional answer model is Meta’s Llama 3.2 instruct line.
        See the{" "}
        <a
          href="https://github.com/meta-llama/llama-models/blob/main/models/llama3_2/LICENSE"
          target="_blank"
          rel="noopener noreferrer"
        >
          Llama 3.2 Community License Agreement
        </a>{" "}
        and{" "}
        <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">
          Meta Privacy Policy
        </a>
        .
      </p>
      <p>
        <strong>BAAI (BGE embeddings):</strong> Embeddings use the Beijing Academy of Artificial
        Intelligence BGE model. License and attribution are described on the{" "}
        <a
          href="https://huggingface.co/BAAI/bge-small-en-v1.5"
          target="_blank"
          rel="noopener noreferrer"
        >
          BAAI/bge-small-en-v1.5 model card (Hugging Face)
        </a>
        .
      </p>
      <p>
        Third-party model weights are subject to their own licenses and notices, which differ from
        Cloudflare’s platform terms. Operators should have this section reviewed by counsel for their
        jurisdiction and deployment.
      </p>

      <h2>Storage and processing</h2>
      <p>
        Data may be stored and processed on cloud infrastructure (for example, edge networks,
        object storage, and managed databases) in regions chosen by the operator. Features described
        as a “private cloud neural index” mean indexing and related inference run in our controlled
        cloud environment—not on your device alone—subject to our security practices.
      </p>

      <h2>Retention</h2>
      <p>
        We retain information as long as your account is active and as needed to provide the Service,
        comply with legal obligations, resolve disputes, and enforce agreements. You may request
        deletion subject to applicable law and technical constraints.
      </p>

      <h2>Sharing</h2>
      <p>
        We do not sell your personal information. We may share data with subprocessors that help us
        host or operate the Service (under contractual safeguards), when required by law, or to
        protect rights and safety.
      </p>

      <h2>Security</h2>
      <p>
        We implement reasonable technical and organizational measures. No method of transmission or
        storage is completely secure; use the Service at your own risk.
      </p>

      <h2>Your rights</h2>
      <p>
        Depending on your location, you may have rights to access, correct, delete, or export personal
        data, and to object to or restrict certain processing. Contact the operator to exercise these
        rights. You may lodge a complaint with a supervisory authority where applicable.
      </p>

      <h2>Cookies and similar technologies</h2>
      <p>
        We may use cookies, local storage, or similar technologies for session management, security,
        and preferences. Third-party analytics or CAPTCHA providers may set their own cookies if
        enabled in your deployment.
      </p>

      <h2>Children</h2>
      <p>
        The Service is not directed at children under 13 (or the minimum age in your jurisdiction).
        We do not knowingly collect personal information from children.
      </p>

      <h2>International transfers</h2>
      <p>
        If you access the Service from outside the operator’s primary region, your data may be
        transferred across borders. We use appropriate safeguards where required.
      </p>

      <h2>Changes</h2>
      <p>
        We may update this policy. We will post the revised policy with an updated date. Material
        changes may require additional notice where required by law.
      </p>

      <h2>Contact</h2>
      <p>
        For privacy requests, contact the operator at the support email published in the application
        or on the operator’s website. See also our <Link to="/terms">Terms of Service</Link>.
      </p>
    </LegalDocument>
  );
}
